<div class="aca-admin aca-ins">
  <aca-page-header icon="insights" [title]="'DMS.INSIGHTS.TITLE' | translate" [subtitle]="'DMS.INSIGHTS.SUBTITLE' | translate">
    <button type="button" class="aca-ins__outline" (click)="reload()" [disabled]="loading()">
      <mat-icon>refresh</mat-icon>{{ 'DMS.INSIGHTS.REFRESH' | translate }}
    </button>
  </aca-page-header>

  <p class="aca-ins__note"><mat-icon>visibility</mat-icon>{{ 'DMS.INSIGHTS.SCOPE_NOTE' | translate }}</p>

  <mat-progress-bar mode="indeterminate" *ngIf="loading()" />

  <aca-page-state
    *ngIf="failed() && !loading()"
    state="error"
    [title]="'DMS.INSIGHTS.LOAD_FAILED' | translate"
    [message]="'DMS.INSIGHTS.LOAD_FAILED_HINT' | translate"
    [actionLabel]="'DMS.INSIGHTS.REFRESH' | translate"
    (action)="reload()"
  />

  <ng-container *ngIf="!loading() && !failed()">
    <!-- ── headline figures ───────────────────────────────────────────────── -->
    <div class="aca-ins__tiles">
      <div class="aca-ins__tile">
        <span class="aca-ins__tilevalue">{{ docs()?.total ?? 0 | number }}</span>
        <span class="aca-ins__tilelabel">{{ 'DMS.INSIGHTS.TILE_FILED' | translate }}</span>
      </div>
      <div class="aca-ins__tile">
        <span class="aca-ins__tilevalue">{{ thisMonth() | number }}</span>
        <span class="aca-ins__tilelabel">{{ 'DMS.INSIGHTS.TILE_THIS_MONTH' | translate }}</span>
      </div>
      <ng-container *ngIf="workflowSettings.enabled() && flows() as wf">
        <div class="aca-ins__tile">
          <span class="aca-ins__tilevalue">{{ wf.running | number }}</span>
          <span class="aca-ins__tilelabel">{{ 'DMS.INSIGHTS.TILE_RUNNING' | translate }}</span>
        </div>
        <div class="aca-ins__tile">
          <span class="aca-ins__tilevalue">{{ duration(wf.averageMs) }}</span>
          <span class="aca-ins__tilelabel">{{ 'DMS.INSIGHTS.TILE_AVERAGE' | translate }}</span>
          <span class="aca-ins__tilesub" *ngIf="wf.medianMs !== null">{{
            'DMS.INSIGHTS.TILE_MEDIAN' | translate: { p0: duration(wf.medianMs) }
          }}</span>
        </div>
      </ng-container>
    </div>

    <p class="aca-ins__note" *ngIf="docs()?.truncated">
      <mat-icon>info</mat-icon>{{ 'DMS.INSIGHTS.TRUNCATED' | translate: { p0: (docs()?.total ?? 0 | number) } }}
    </p>

    <!-- ── documents per month ────────────────────────────────────────────── -->
    <section class="aca-ins__card" *ngIf="months().length">
      <header class="aca-ins__cardhead">
        <h2 class="aca-ins__cardtitle"><mat-icon>bar_chart</mat-icon>{{ 'DMS.INSIGHTS.BY_MONTH' | translate }}</h2>
        <span class="aca-ins__cardmeta">{{ 'DMS.INSIGHTS.YEAR_TOTAL' | translate: { p0: (yearTotal() | number) } }}</span>
      </header>
      <div class="aca-ins__columns" role="list">
        <div
          class="aca-ins__column"
          role="listitem"
          *ngFor="let m of months(); let last = last"
          [matTooltip]="(monthDate(m) | acaDate: 'MMMM y') + ': ' + (m.count | number)"
          [attr.aria-label]="(monthDate(m) | acaDate: 'MMMM y') + ': ' + (m.count | number)"
        >
          <span class="aca-ins__columnvalue" *ngIf="last || m.count === monthPeak()">{{ m.count | number }}</span>
          <span class="aca-ins__columnbar" [class.aca-ins__columnbar--now]="last" [style.height.%]="columnHeight(m)"></span>
          <span class="aca-ins__columnlabel">{{ monthDate(m) | acaDate: 'MMM' }}</span>
        </div>
      </div>
    </section>

    <!-- ── documents by field ─────────────────────────────────────────────── -->
    <div class="aca-ins__grid" *ngIf="docs() as d">
      <section class="aca-ins__card" *ngFor="let b of breakdowns">
        <header class="aca-ins__cardhead">
          <h2 class="aca-ins__cardtitle">
            <mat-icon>{{ b.icon }}</mat-icon
            >{{ b.titleKey | translate }}
          </h2>
        </header>
        <p class="aca-ins__empty" *ngIf="!buckets(b.key).length">{{ 'DMS.INSIGHTS.NO_DOCUMENTS' | translate }}</p>
        <ng-container *ngFor="let bucket of buckets(b.key)">
          <button
            type="button"
            class="aca-ins__row"
            [class.aca-ins__row--link]="b.filter && bucket.value"
            [disabled]="!b.filter || !bucket.value"
            [attr.aria-label]="(label(bucket, b) | translate) + ': ' + bucket.count"
            (click)="open(bucket, b)"
          >
            <span class="aca-ins__rowlabel" [class.aca-ins__rowlabel--muted]="!bucket.value">{{ label(bucket, b) | translate }}</span>
            <span class="aca-ins__track"
              ><span class="aca-ins__bar" [class.aca-ins__bar--muted]="!bucket.value" [style.width.%]="share(bucket, buckets(b.key))"></span
            ></span>
            <span class="aca-ins__rowvalue"
              >{{ bucket.count | number }} <small>{{ percent(bucket) }}%</small></span
            >
          </button>
        </ng-container>
      </section>
    </div>

    <!-- ── workflow ───────────────────────────────────────────────────────── -->
    <ng-container *ngIf="workflowSettings.enabled()">
      <section class="aca-ins__card" *ngIf="flows() as wf; else flowsUnavailable">
        <header class="aca-ins__cardhead">
          <h2 class="aca-ins__cardtitle"><mat-icon>schedule</mat-icon>{{ 'DMS.INSIGHTS.FLOW_TITLE' | translate }}</h2>
          <span class="aca-ins__cardmeta">
            {{ 'DMS.INSIGHTS.FLOW_COUNTS' | translate: { p0: wf.running, p1: wf.completed, p2: wf.cancelled } }}
          </span>
        </header>

        <p class="aca-ins__empty" *ngIf="!wf.byFlow.length">{{ 'DMS.INSIGHTS.FLOW_NONE' | translate }}</p>
        <table class="aca-ins__table" *ngIf="wf.byFlow.length">
          <thead>
            <tr>
              <th>{{ 'DMS.INSIGHTS.COL_FLOW' | translate }}</th>
              <th class="aca-ins__num">{{ 'DMS.INSIGHTS.COL_DONE' | translate }}</th>
              <th class="aca-ins__num">{{ 'DMS.INSIGHTS.COL_AVERAGE' | translate }}</th>
              <th class="aca-ins__num">{{ 'DMS.INSIGHTS.COL_MEDIAN' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let f of wf.byFlow">
              <td>{{ flowName(f.name, f.builtin) | translate }}</td>
              <td class="aca-ins__num">{{ f.completed | number }}</td>
              <td class="aca-ins__num">{{ duration(f.averageMs) }}</td>
              <td class="aca-ins__num">{{ duration(f.medianMs) }}</td>
            </tr>
          </tbody>
        </table>

        <ng-container *ngIf="wf.oldest.length">
          <h3 class="aca-ins__subtitle">{{ 'DMS.INSIGHTS.OLDEST' | translate }}</h3>
          <button type="button" class="aca-ins__flow" *ngFor="let o of wf.oldest" (click)="openFlow(o)" [disabled]="!o.processId">
            <mat-icon>hourglass_top</mat-icon>
            <span class="aca-ins__flowname">{{ flowName(o.name, o.builtin) | translate }}</span>
            <span class="aca-ins__flowdate">{{ o.startedAt | acaDate: 'd MMM y' }}</span>
            <span class="aca-ins__flowage">{{ 'DMS.INSIGHTS.WAITING' | translate: { p0: age(o) } }}</span>
            <mat-icon class="aca-ins__go">chevron_right</mat-icon>
          </button>
        </ng-container>

        <p class="aca-ins__note" *ngIf="wf.truncated"><mat-icon>info</mat-icon>{{ 'DMS.INSIGHTS.FLOW_TRUNCATED' | translate }}</p>
        <p class="aca-ins__note" *ngIf="wf.historyMissing"><mat-icon>info</mat-icon>{{ 'DMS.INSIGHTS.FLOW_HISTORY_MISSING' | translate }}</p>
      </section>
      <ng-template #flowsUnavailable>
        <p class="aca-ins__note"><mat-icon>info</mat-icon>{{ 'DMS.INSIGHTS.FLOW_UNAVAILABLE' | translate }}</p>
      </ng-template>
    </ng-container>
  </ng-container>
</div>
